/*
 * Shared short-lived toast — a message that shows then auto-clears after a
 * fixed duration, with a pending timeout always cleared before a re-show or
 * on unmount. Used by every Deckorate feature hook that reports success via
 * a toast (useMyBrandTemplates, useShareExport, ...).
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface TimedToast {
  readonly toast: string | null;
  readonly showToast: (message: string) => void;
}

export function useTimedToast(durationMs: number): TimedToast {
  const [toast, setToast] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setToast(null), durationMs);
  }, [durationMs]);

  return { toast, showToast };
}
